import { lazy, Suspense, Component } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'
import { ThemeProvider } from './context/ThemeContext'
import Layout from './components/Layout'
import TabNav from './components/TabNav'
import AskAI from './components/AskAI'
import LoginPage from './pages/LoginPage'
import ProductionDashboard from './pages/ProductionDashboard'
import LeeView from './pages/LeeView'
import FinancialsOverview from './pages/FinancialsOverview'
import LOEDetail from './pages/LOEDetail'
import LOEMonthDetail from './pages/LOEMonthDetail'
import RevenueDetail from './pages/RevenueDetail'
import WellDetail from './pages/WellDetail'
import WorkoverOverview from './pages/WorkoverOverview'
import WorkoverDetail from './pages/WorkoverDetail'
import WorkoverAnalytics from './pages/WorkoverAnalytics'
import WorkoverHistory from './pages/WorkoverHistory'
import WorkoverSchedule from './pages/WorkoverSchedule'
import WellsDownPage from './pages/WellsDownPage'
import WellAnalysis from './pages/WellAnalysis'
import WellReview from './pages/WellReview'
import TankDashboard from './pages/TankDashboard'
import DownloadsDashboard from './pages/DownloadsDashboard'
import HedgeOverview from './pages/HedgeOverview'
import HedgeScenarios from './pages/HedgeScenarios'
import HedgePositions from './pages/HedgePositions'
import HedgeOptionsMonitor from './pages/HedgeOptionsMonitor'

// Lazy-load WellMap with auto-retry on stale chunk failures (e.g. after deploy)
const WellMap = lazy(() =>
    import('./pages/WellMap').catch(() => {
        // Chunk hash changed after deploy — force reload to get new index.html
        // Guard against infinite reload loops with a sessionStorage flag
        const reloaded = sessionStorage.getItem('map_chunk_reload')
        if (!reloaded) {
            sessionStorage.setItem('map_chunk_reload', '1')
            window.location.reload()
        }
        throw new Error('App was updated. Please refresh the page.')
    })
)

// Clear reload guard on successful load
if (sessionStorage.getItem('map_chunk_reload')) {
    sessionStorage.removeItem('map_chunk_reload')
}

// Error boundary to prevent map issues from crashing the whole app
class MapErrorBoundary extends Component {
    state = { hasError: false, error: null }
    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="text-center py-12">
                    <p className="text-lg font-semibold text-red-400">Map failed to load</p>
                    <p className="mt-2 text-sm text-gray-400">{this.state.error?.message}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-aviator-600 text-white rounded hover:bg-aviator-700"
                    >Retry</button>
                </div>
            )
        }
        return this.props.children
    }
}

const MapLoader = () => (
    <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
    </div>
)

const SafeWellMap = () => (
    <MapErrorBoundary>
        <Suspense fallback={<MapLoader />}>
            <WellMap />
        </Suspense>
    </MapErrorBoundary>
)

function App() {
    const isAuthenticated = useIsAuthenticated()

    if (!isAuthenticated) {
        return <LoginPage />
    }

    return (
        <ThemeProvider>
            <Layout>
                <TabNav />
                <Routes>
                    {/* Production */}
                    <Route path="/" element={<ProductionDashboard />} />
                    <Route path="/production" element={<ProductionDashboard />} />

                    {/* LEEVIEW - raw per-well daily oil table */}
                    <Route path="/leeview" element={<LeeView />} />

                    {/* Map */}
                    <Route path="/map" element={<SafeWellMap />} />

                    {/* Financials - overview + drill-downs */}
                    <Route path="/financials" element={<FinancialsOverview />} />
                    <Route path="/financials/loe" element={<LOEDetail />} />
                    <Route path="/financials/loe/month/:yearMonth" element={<LOEMonthDetail />} />
                    <Route path="/financials/loe/:category" element={<LOEDetail />} />
                    <Route path="/financials/revenue" element={<RevenueDetail />} />

                    {/* Workovers - overview + drill-downs */}
                    <Route path="/workovers" element={<WorkoverOverview />} />
                    <Route path="/workovers/detail/:id" element={<WorkoverDetail />} />
                    <Route path="/workovers/analytics" element={<WorkoverAnalytics />} />
                    <Route path="/workovers/history" element={<WorkoverHistory />} />
                    <Route path="/workovers/wells-down" element={<WellsDownPage />} />
                    <Route path="/workovers/schedule" element={<WorkoverSchedule />} />

                    {/* Sales / Tank */}
                    <Route path="/tanks" element={<TankDashboard />} />

                    {/* Well Analysis */}
                    <Route path="/well-analysis" element={<WellAnalysis />} />

                    {/* Well Review — per-lease production, workovers, P&L */}
                    <Route path="/well-review" element={<WellReview />} />

                    {/* Hedge — overview, scenarios, positions CRUD, options */}
                    <Route path="/hedge" element={<HedgeOverview />} />
                    <Route path="/hedge/scenarios" element={<HedgeScenarios />} />
                    <Route path="/hedge/positions" element={<HedgePositions />} />
                    <Route path="/hedge/options" element={<HedgeOptionsMonitor />} />

                    {/* Downloads — ComboCurve uploads + investor quarterly */}
                    <Route path="/downloads" element={<DownloadsDashboard />} />

                    {/* Well drill-down */}
                    <Route path="/wells/:wellId" element={<WellDetail />} />
                </Routes>
                <AskAI />
            </Layout>
        </ThemeProvider>
    )
}

export default App
